// Operator › Templates (`/operator/templates`; DC-03, 02 §DE configuration "Catalog"): the agent
// template catalog an agency user sees, the platform's templates and its own agency's
// (`GET /v1/operator/agent-templates`, one call), each with its scope, class, status and current
// version. Template authors create a template head here (`POST /v1/operator/agent-templates`);
// its versions are cut on the template's page.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  SearchField,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Toolbar,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowUpRightIcon, GitBranchIcon, LayersIcon, PlusIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../../api/client";
import type { AgentTemplate, MeResponse } from "../../../api/types";
import {
  OpsBody,
  OpsFrame,
  operatorCrumb,
  RowActions,
  RowLinkAction,
  TABLE_ROW_HOVER,
} from "../../ops-ux";
import { LoadError, Loading } from "../../screen-parts";
import {
  CLASS_LABEL,
  isTemplateAuthor,
  problemLines,
  SCOPE_LABEL,
  TemplateStatusBadge,
} from "./template-parts";
import { agentTemplatesQuery, templateKeys } from "./template-queries";

export function TemplatesPage({ api, me }: { api: Api; me: MeResponse }) {
  const list = useQuery(agentTemplatesQuery(api));
  const author = isTemplateAuthor(me);
  const rows = list.data?.data ?? [];
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const shown = needle
    ? rows.filter((t) => `${t.name} ${t.key}`.toLowerCase().includes(needle))
    : rows;
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Templates" }]}
        icon={LayersIcon}
        title="Templates"
        count={list.isPending ? undefined : rows.length}
        actions={author ? <NewTemplateDialog api={api} me={me} /> : undefined}
      />
      <Toolbar bleed>
        <SearchField
          placeholder="Search templates…"
          aria-label="Search templates"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
          The current version is the one deploys use; a version publishes once its evals pass.
        </span>
      </Toolbar>
      <OpsBody>
        {list.isPending ? (
          <Loading label="Loading templates" />
        ) : list.isError ? (
          <LoadError what="Templates" error={list.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={LayersIcon}
            title="No templates yet"
            description={
              author
                ? "Create a template, then cut its first draft version."
                : "Templates your agency's authors create appear here."
            }
          />
        ) : (
          <Table data-slot="agent-templates">
            <TableCaption>
              Platform templates and your agency's; the current version is the one deploys use.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Template</TableHead>
                <TableHead scope="col">Key</TableHead>
                <TableHead scope="col">Scope</TableHead>
                <TableHead scope="col">Class</TableHead>
                <TableHead scope="col">Status</TableHead>
                <TableHead scope="col">Current version</TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((t) => (
                <TemplateRow key={t.id} t={t} />
              ))}
            </TableBody>
          </Table>
        )}
      </OpsBody>
    </OpsFrame>
  );
}

function TemplateRow({ t }: { t: AgentTemplate }) {
  return (
    <TableRow data-template={t.key} className={TABLE_ROW_HOVER}>
      <TableHead scope="row" className="font-normal">
        <Link
          to="/operator/templates/$templateId"
          params={{ templateId: t.id }}
          className="font-medium underline-offset-4 hover:underline"
        >
          {t.name}
        </Link>
      </TableHead>
      <TableCell>
        <code className="text-xs">{t.key}</code>
      </TableCell>
      <TableCell>{SCOPE_LABEL[t.scope]}</TableCell>
      <TableCell>{CLASS_LABEL[t.template_class]}</TableCell>
      <TableCell>
        <TemplateStatusBadge status={t.status} />
      </TableCell>
      <TableCell>
        {t.current_version_id ? (
          <Link
            to="/operator/templates/$templateId/versions/$versionId"
            params={{ templateId: t.id, versionId: t.current_version_id }}
            className="underline-offset-4 hover:underline"
          >
            Open current version
          </Link>
        ) : (
          <span className="text-muted-foreground">None</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <RowActions>
          <RowLinkAction
            icon={ArrowUpRightIcon}
            label={`Open ${t.name}`}
            render={<Link to="/operator/templates/$templateId" params={{ templateId: t.id }} />}
          />
          {t.current_version_id ? (
            <RowLinkAction
              icon={GitBranchIcon}
              label={`Open current version of ${t.name}`}
              render={
                <Link
                  to="/operator/templates/$templateId/versions/$versionId"
                  params={{ templateId: t.id, versionId: t.current_version_id }}
                />
              }
            />
          ) : null}
        </RowActions>
      </TableCell>
    </TableRow>
  );
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}

type Scope = AgentTemplate["scope"];
type Kind = AgentTemplate["kind"];
type Class = AgentTemplate["template_class"];

/** "New template": a template head (no version yet), then its page to cut the first draft. */
function NewTemplateDialog({ api, me }: { api: Api; me: MeResponse }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [scope, setScope] = useState<Scope>("agency");
  const [kind, setKind] = useState<Kind>("persistent");
  const [cls, setCls] = useState<Class>("managed");
  const ids = { name: useId(), key: useId(), scope: useId(), kind: useId(), cls: useId() };
  const agencyId = me.agency?.agency_id;

  const create = useMutation({
    mutationFn: () =>
      api.client.operator.agentTemplates.create({
        idempotencyKey: newIdempotencyKey(),
        body: {
          scope,
          ...(scope === "agency" && agencyId ? { agency_id: agencyId } : {}),
          key,
          name: name.trim(),
          kind,
          template_class: scope === "agency" ? "managed" : cls,
        },
      }),
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: templateKeys.all() });
      setOpen(false);
      void navigate({ to: "/operator/templates/$templateId", params: { templateId: t.id } });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) create.reset();
      }}
    >
      <Button size="sm" onClick={() => setOpen(true)}>
        <PlusIcon aria-hidden="true" />
        New template
      </Button>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>New template</DialogTitle>
            <DialogDescription>
              Creates the template. Its first version is a draft you cut on the next page.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
              <Input
                id={ids.name}
                required
                maxLength={200}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!keyTouched) setKey(slugify(e.target.value));
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.key}>Key</FieldLabel>
              <Input
                id={ids.key}
                required
                pattern="[a-z0-9][a-z0-9-]{1,62}"
                value={key}
                onChange={(e) => {
                  setKeyTouched(true);
                  setKey(e.target.value);
                }}
              />
              <FieldDescription>Lowercase letters, digits and dashes; unique.</FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.scope}>Scope</FieldLabel>
              <NativeSelect
                id={ids.scope}
                value={scope}
                onChange={(e) => setScope(e.target.value as Scope)}
              >
                <NativeSelectOption value="agency">Your agency only</NativeSelectOption>
                <NativeSelectOption value="platform">Platform (every agency)</NativeSelectOption>
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.kind}>Kind</FieldLabel>
              <NativeSelect
                id={ids.kind}
                value={kind}
                onChange={(e) => setKind(e.target.value as Kind)}
              >
                <NativeSelectOption value="persistent">Persistent (a DE)</NativeSelectOption>
                <NativeSelectOption value="ephemeral">Ephemeral (one task)</NativeSelectOption>
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.cls}>Class</FieldLabel>
              <NativeSelect
                id={ids.cls}
                value={scope === "agency" ? "managed" : cls}
                disabled={scope === "agency"}
                onChange={(e) => setCls(e.target.value as Class)}
              >
                <NativeSelectOption value="managed">{CLASS_LABEL.managed}</NativeSelectOption>
                <NativeSelectOption value="basic">Basic</NativeSelectOption>
                <NativeSelectOption value="empty">Empty</NativeSelectOption>
              </NativeSelect>
              <FieldDescription>Basic and empty templates are platform-owned.</FieldDescription>
            </Field>
            {create.isError ? (
              <FieldError>
                {problemLines(create.error, "The template could not be created.").join(" ")}
              </FieldError>
            ) : null}
          </FieldGroup>
          <DialogFooter>
            <Button type="submit" disabled={create.isPending || !name.trim() || !key}>
              {create.isPending ? "Creating…" : "Create template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
